import { useConfig } from '@marine/configs';
import { useMemo } from 'react';
import {
  buildFileUrlFromId,
  buildImageSources,
  buildImageUrlFormats,
  type ImageSources,
  type ImageUrlFormats,
  signFileUrl,
} from './useFileUrlWithClerkToken';
import { useImageUrlTokenQuery } from './useImageUrlTokenQuery';

type UseFileUrlResult = ImageUrlFormats & {
  isLoading: boolean;
  /**
   * The same URLs as `expo-image` sources, each carrying a token-free `cacheKey`. Mobile callers
   * should pass these to `<Image source={...} />` rather than `{ uri }` built from the strings above,
   * so a token refresh doesn't force every visible picture to download and decode again.
   */
  sources: ImageSources;
};

// http://beta-api.marinedatacloud.com/web-api/files/86/pexels-ryan-west-1719648.jpg?format=card

/**
 * The signed URLs for one file id, in every format the file service renders.
 *
 * The only asynchronous part of this is the Clerk token, and that is shared app-wide by
 * [useImageUrlTokenQuery] — the URLs themselves are string concatenation, so they're derived here
 * synchronously instead of being cached per file id. That matters on mobile: the old per-file query
 * put nine copies of a JWT into the query cache for every image the user scrolled past, and the whole
 * cache is serialized to MMKV on a throttle, so scrolling a large folder meant re-stringifying a
 * steadily growing blob on the JS thread.
 */
export const useImageUrlsQuery = (fileId?: string | number | null): UseFileUrlResult => {
  const { mdcApiHost } = useConfig();
  const { data: token, isPending } = useImageUrlTokenQuery({ enabled: !!fileId });

  const { urls, sources } = useMemo(() => {
    if (!fileId || !token) {
      return { urls: {} as ImageUrlFormats, sources: {} as ImageSources };
    }

    const signedUrl = signFileUrl(buildFileUrlFromId(mdcApiHost, fileId), token);
    const imageUrls = buildImageUrlFormats(signedUrl);

    return { urls: imageUrls, sources: buildImageSources(imageUrls, fileId) };
  }, [mdcApiHost, fileId, token]);

  return {
    // A disabled query stays pending forever, so without the id check a caller with no file to show
    // would gate a spinner on this and never stop spinning.
    isLoading: !!fileId && isPending,
    sources,
    ...urls,
  };
};
